import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Smartphone, ShieldCheck, AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { API } from "@/lib/api";
import { adoptSession } from "../lib/session";
import {
  canPair,
  generateDeviceKey,
  pairingChallenge,
  readPairingFromHash,
  signChallenge,
} from "../lib/device-key";
import { useTaal } from "@/contexts/TaalContext";

/**
 * The phone's side of pairing. Reached by scanning the QR on the desktop.
 *
 * Nothing here signs you in on its own. The phone proves it holds a key it
 * made itself, shows a number, and waits. Only the owner confirming that
 * number on the desktop turns it into access, which is what makes a
 * photographed QR worthless.
 */
export default function PairPage() {
  const { t } = useTaal();
  const navigate = useNavigate();
  const [phase, setPhase] = useState("starting");
  const [code, setCode] = useState(null);
  const [error, setError] = useState(null);
  const started = useRef(false);

  const run = useCallback(async () => {
    const pairing = readPairingFromHash(window.location.hash);
    if (!pairing) {
      setPhase("error");
      setError("This link is missing its pairing details. Scan the code on your computer again.");
      return;
    }
    if (!canPair(window.crypto)) {
      setPhase("error");
      setError(
        "This browser cannot create a key here. That usually means the page was opened over http instead of https."
      );
      return;
    }

    let key;
    try {
      key = await generateDeviceKey(window.crypto.subtle);
      const signature = await signChallenge(
        window.crypto.subtle,
        key.privateKey,
        pairingChallenge(pairing.offer, key.publicKeySpkiB64)
      );
      const claim = await fetch(`${API}/devices/pairing/claim`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          offer: pairing.offer,
          secret: pairing.secret,
          public_key: key.publicKeySpkiB64,
          signature,
          name: navigator.userAgent.includes("iPhone") ? "iPhone" : t("Telefoon"),
        }),
      });
      if (!claim.ok) {
        const body = await claim.json().catch(() => ({}));
        throw new Error(
          typeof body.detail === "string" ? body.detail : t("Deze koppeling staat niet meer open.")
        );
      }
      const claimed = await claim.json();
      setCode(claimed.code);
      setPhase("waiting");
    } catch (e) {
      setPhase("error");
      setError(e.message || t("Koppelen kon niet starten."));
      return;
    }

    // Poll until the owner confirms on the desktop, or the offer runs out.
    for (let attempt = 0; attempt < 120; attempt += 1) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      let response;
      try {
        response = await fetch(`${API}/devices/pairing/poll`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ offer: pairing.offer, secret: pairing.secret }),
        });
      } catch {
        continue; // a phone loses its network for a moment; that is not a failure
      }
      if (response.status === 404) {
        setPhase("error");
        setError("This pairing has expired or was refused on the computer.");
        return;
      }
      if (!response.ok) continue;
      const state = await response.json();
      if (state.state === "confirmed") {
        setPhase("done");
        // A full load, not a route change: the auth context asks who you are
        // once, on mount, and a client-side navigate never gets it to ask
        // again. That left the phone with real tokens, real data, and a
        // screen saying nobody was signed in.
        setTimeout(() => adoptSession(state), 900);
        return;
      }
    }
    setPhase("error");
    setError("Nobody confirmed this on the computer in time.");
  }, [t]);

  useEffect(() => {
    // React runs effects twice in development. Pairing is single use, so a
    // second run would claim an offer that the first one already took.
    if (started.current) return;
    started.current = true;
    run();
  }, [run]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 pt-[var(--veilig-boven)] pb-[env(safe-area-inset-bottom)]">
      <div className="w-full max-w-sm text-center space-y-6" data-testid="pair-page">
        <div className="flex justify-center">
          <div className="h-14 w-14 rounded-2xl bg-[hsl(var(--accent))]/10 flex items-center justify-center">
            {phase === "done" ? (
              <ShieldCheck className="h-7 w-7 text-ok" />
            ) : phase === "error" ? (
              <AlertTriangle className="h-7 w-7 text-corona" />
            ) : (
              <Smartphone className="h-7 w-7 text-[hsl(var(--accent))]" />
            )}
          </div>
        </div>

        {phase === "starting" && (
          <p className="text-sm text-muted-foreground" data-testid="pair-starting">
            {t("Een sleutel maken op deze telefoon...")}
          </p>
        )}

        {phase === "waiting" && (
          <>
            <div>
              <p className="text-sm text-muted-foreground mb-3">
                {t("Kijk of je computer hetzelfde getal toont en bevestig het daar.")}
              </p>
              <p className="text-4xl font-mono tracking-widest" data-testid="pair-code">
                {code?.slice(0, 3)} {code?.slice(3)}
              </p>
            </div>
            <p className="text-xs text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin" />
              {t("Wachten tot je op de computer bevestigt")}
            </p>
            <p className="text-xs text-corona">
              {t("Zijn de getallen anders, bevestig dan niet. Iemand anders probeert te koppelen.")}
            </p>
          </>
        )}

        {phase === "done" && (
          <p className="text-sm" data-testid="pair-done">
            {t("Gekoppeld. Novamund wordt geopend.")}
          </p>
        )}

        {phase === "error" && (
          <>
            <p className="text-sm text-corona" data-testid="pair-error">
              {error}
            </p>
            <Button variant="outline" onClick={() => navigate("/")}>
              {t("Terug naar Novamund")}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
